import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import './LoginForm.css';

const loginSchema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
}).required();

function LoginForm() {
  const { login, verify2FA, cancel2FA, requires2FA, error, clearError, isSubmitting } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const codeInputRefs = useRef([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Focus first code input when 2FA is required
  useEffect(() => {
    if (requires2FA && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [requires2FA]);

  const onSubmit = async (data) => {
    clearError();
    await login(data.email, data.password);
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handleVerify2FA(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!verificationCode[index] && index > 0) {
        codeInputRefs.current[index - 1]?.focus();
      }
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newCode = [...verificationCode];
        digits.forEach((digit, i) => {
          if (i < 6) newCode[i] = digit;
        });
        setVerificationCode(newCode);
        if (digits.length === 6) {
          handleVerify2FA(newCode.join(''));
        } else {
          codeInputRefs.current[digits.length]?.focus();
        }
      });
    }
  };

  const handleVerify2FA = async (code) => {
    clearError();
    await verify2FA(code);
  };

  const handleCancel2FA = () => {
    setVerificationCode(['', '', '', '', '', '']);
    cancel2FA();
  };

  // 2FA Verification Form
  if (requires2FA) {
    return (
      <div className="login-form twofa-form">
        <div className="twofa-header">
          <div className="twofa-icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h2>Two-Factor Authentication</h2>
          <p>Enter the 6-digit code sent to your email</p>
        </div>

        {error && (
          <div className="login-form-error" role="alert">
            {error}
          </div>
        )}

        <div className="verification-code-inputs">
          {verificationCode.map((digit, index) => (
            <input
              key={index}
              ref={el => codeInputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className="code-input"
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleCodeKeyDown(index, e)}
              disabled={isSubmitting}
              autoComplete="one-time-code"
            />
          ))}
        </div>

        <button
          type="button"
          className="login-button"
          onClick={() => handleVerify2FA(verificationCode.join(''))}
          disabled={isSubmitting || verificationCode.some(d => d === '')}
        >
          {isSubmitting ? 'Verifying...' : 'Verify Code'}
        </button>

        <button
          type="button"
          className="cancel-button"
          onClick={handleCancel2FA}
          disabled={isSubmitting}
        >
          Back to Login
        </button>
      </div>
    );
  }

  // Standard Login Form
  return (
    <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {error && (
        <div className="login-form-error" role="alert">
          {error}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="email" className="form-label">
          Email Address
        </label>
        <input
          id="email"
          type="email"
          className={`form-input ${errors.email ? 'form-input-error' : ''}`}
          placeholder="admin@touchlinememories.com"
          autoComplete="email"
          disabled={isSubmitting}
          {...register('email')}
        />
        {errors.email && (
          <span className="form-error-message" role="alert">
            {errors.email.message}
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="password" className="form-label">
          Password
        </label>
        <div className="password-input-wrapper">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            className={`form-input ${errors.password ? 'form-input-error' : ''}`}
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={isSubmitting}
            {...register('password')}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {errors.password && (
          <span className="form-error-message" role="alert">
            {errors.password.message}
          </span>
        )}
      </div>

      <button
        type="submit"
        className="login-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Signing in...' : 'Sign In'}
      </button>

      <div className="login-form-links">
        <a href="/forgot-password" className="forgot-password-link">
          Forgot your password?
        </a>
      </div>
    </form>
  );
}

export default LoginForm;
